'use client';

import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import {
  getMemberAddressService,
  type SeedingDeliveryAddressItem,
} from '@musinsa/services-curator';
import { useCallback, useEffect, useRef } from 'react';
import { useUpdateDeliveryAddress } from './useSeedingMutations';
import { setSelectedDeliveryAddress } from './useSelectedDeliveryAddress';

/** 무신사 배송지 목록·선택 페이지 (order-group). 쿠키 인증 same-origin. */
const ADDRESS_ORDER_PATH = '/order-service/my/addresses/order';

export type CuratorAddressEventType = 'ADDED' | 'SELECTED';

export interface CuratorAddressResult {
  selectedAddressId: string;
  eventType: CuratorAddressEventType;
  /** order-service 에서 조회·매핑한 배송지 (조회 실패 시 undefined). */
  address?: SeedingDeliveryAddressItem;
}

interface UseCuratorAddressSelectOptions {
  /** 선택 배송지를 overlay 할 캠페인 ID — `setSelectedDeliveryAddress` 키. */
  campaignId: number;
  /** 배송지 선택/추가 + 조회 완료 시 호출 (postMessage 수신 후). */
  onResult?: (result: CuratorAddressResult) => void;
  /**
   * 선택 배송지를 서버에 PUT 영속화할지 여부 (기본 true).
   * 신청 전(모집형 recruiting/BEFORE_APPLY) 캠페인은 참여(participation)가 없어 PUT 이 404 →
   * false 로 skip 한다. 이 경우 표시·신청 payload 는 인메모리 store(setSelectedDeliveryAddress)가
   * 담당하며(overlaySelectedAddress → registered/zipcode 반영), 신청 제출이 deliveryAddress 를
   * 통째로 실어 보낸다.
   */
  persist?: boolean;
}

const isAddressMessage = (
  data: unknown,
): data is {
  type: 'DELIVERY_ADDRESS_ADDED' | 'DELIVERY_ADDRESS_SELECTED';
  selectedAddressId: string;
} => {
  if (typeof data !== 'object' || data === null) return false;
  const r = data as Record<string, unknown>;
  return (
    (r.type === 'DELIVERY_ADDRESS_ADDED' || r.type === 'DELIVERY_ADDRESS_SELECTED') &&
    typeof r.selectedAddressId === 'string'
  );
};

/**
 * 시딩 상세 — 배송지 변경 진입 (무신사 전용).
 *
 * 확정 스펙(opener 방식): 무신사 배송지 페이지를 named `window.open` 으로 띄우면 order-group 이
 * `window.opener` 존재를 보고 popup 모드로 동작한다. 변경하기/X → 새 창에서 `window.close()`,
 * 선택 결과는 `window.opener.postMessage`(targetOrigin '*') 로 직접 전달된다. 발행 측이 '*' 이므로
 * 수신 측은 `e.origin` 이 `.musinsa.com` 인지 반드시 검증한다.
 *
 * 수신한 `selectedAddressId` 로 order-service(`getMemberAddress`)를 조회해 배송지 전체를 얻고,
 * `setSelectedDeliveryAddress` 로 상세 화면 배송지 표시를 갱신한다(신청/수락 payload 도 이 값 사용).
 *
 * 무신사 외 서비스(29CM 등)에서는 order-group same-origin 계약이 성립하지 않아 noop. `isAvailable`
 * 로 진입점 노출 여부를 분기한다.
 */
export const useCuratorAddressSelect = ({
  campaignId,
  onResult,
  persist = true,
}: UseCuratorAddressSelectOptions) => {
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;

  // 선택 배송지를 캠페인에 서버 영속화(상세 invalidate) — 라우트 왕복에도 유지. (SID-4048/4114)
  const updateAddressMutation = useUpdateDeliveryAddress();
  const updateAddressMutationRef = useRef(updateAddressMutation);
  updateAddressMutationRef.current = updateAddressMutation;

  // 리스너는 mount 시점에 등록하므로 최신 값을 ref 로 참조 (재구독 없이 갱신).
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const campaignIdRef = useRef(campaignId);
  campaignIdRef.current = campaignId;
  const persistRef = useRef(persist);
  persistRef.current = persist;

  useEffect(() => {
    if (!isMusinsa) return;
    const handleMessage = async (e: MessageEvent) => {
      if (!e.origin.endsWith('.musinsa.com')) return;
      if (!isAddressMessage(e.data)) return;

      const { selectedAddressId } = e.data;
      const eventType: CuratorAddressEventType =
        e.data.type === 'DELIVERY_ADDRESS_ADDED' ? 'ADDED' : 'SELECTED';

      try {
        // order-service 단건 조회 → 상세 화면 배송지 표시·payload 갱신.
        const address = await getMemberAddressService(Number(selectedAddressId));
        setSelectedDeliveryAddress(campaignIdRef.current, address);
        // 캠페인에 배송지 서버 영속화(상세 invalidate → registered/zipcode 반영, 신청 게이트 통과).
        // 신청 전(participation 없음) 캠페인은 PUT 404 → skip. 표시·payload 는 위 store 가 담당.
        if (persistRef.current) {
          updateAddressMutationRef.current.mutate({ campaignId: campaignIdRef.current, address });
        }
        onResultRef.current?.({ selectedAddressId, eventType, address });
      } catch (error) {
        console.warn('[CuratorAddressSelect] getMemberAddress 조회 실패', error);
        onResultRef.current?.({ selectedAddressId, eventType });
      }
    };
    const handler = (e: MessageEvent) => {
      void handleMessage(e);
    };
    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [isMusinsa]);

  const openAddressSelect = useCallback(() => {
    if (!isMusinsa) return;
    const base = process.env.NEXT_PUBLIC_MUSINSA_BASE_URL ?? '';
    // 이름 있는 popup → window.opener 존재(popup 모드). 변경하기/X 시 새 창이 self-close.
    window.open(
      `${base}${ADDRESS_ORDER_PATH}`,
      'CuratorAddressPopup',
      'width=450,height=685,resizable=no',
    );
  }, [isMusinsa]);

  return { openAddressSelect, isAvailable: isMusinsa };
};
